import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import {
  Notification,
  NotificationType,
  NotificationPriority,
  getNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  archiveNotification,
  deleteNotification,
  getUnreadNotificationCount,
  subscribeToNotifications,
  subscribeToUnreadCount,
  createNotification,
  createAutoNotifications,
} from '../services/notificationService';

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  
  // Actions
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  archive: (notificationId: string) => Promise<void>;
  delete: (notificationId: string) => Promise<void>;
  create: (type: NotificationType, title: string, message: string, data?: any, priority?: NotificationPriority) => Promise<string>;
  addNotification: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
  
  // Auto-notifications
  notifyNewSubscriber: (subscriberName: string, publicationName: string) => Promise<string>;
  notifySubscriptionCancelled: (subscriberName: string, publicationName: string) => Promise<string>;
  notifyPaymentReceived: (amount: number, subscriberName: string) => Promise<string>;
  notifyPaymentFailed: (subscriberName: string, amount: number) => Promise<string>;
  notifyPostPublished: (postTitle: string, subscriberCount: number) => Promise<string>;
  notifyRevenueMilestone: (amount: number, milestone: string) => Promise<string>;
  
  // Refresh
  refresh: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load initial notifications
  const loadNotifications = useCallback(async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      setError(null);
      const [notificationsData, unreadCountData] = await Promise.all([
        getNotifications(currentUser.uid),
        getUnreadNotificationCount(currentUser.uid)
      ]);
      
      setNotifications(notificationsData);
      setUnreadCount(unreadCountData);
    } catch (err) {
      console.error('Error loading notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Set up real-time listeners
  useEffect(() => {
    if (!currentUser) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    // Subscribe to notifications
    const unsubscribeNotifications = subscribeToNotifications(
      currentUser.uid,
      (newNotifications) => {
        setNotifications(newNotifications);
      }
    );

    // Subscribe to unread count
    const unsubscribeUnreadCount = subscribeToUnreadCount(
      currentUser.uid,
      (count) => {
        setUnreadCount(count);
      }
    );

    return () => {
      unsubscribeNotifications();
      unsubscribeUnreadCount();
    };
  }, [currentUser]);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await markNotificationAsRead(notificationId);
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, isRead: true, readAt: new Date() }
            : notification
        )
      );
    } catch (err) {
      console.error('Error marking notification as read:', err);
      setError('Failed to mark notification as read');
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    if (!currentUser) return;

    try {
      await markAllNotificationsAsRead(currentUser.uid);
      setNotifications(prev => 
        prev.map(notification => ({ 
          ...notification, 
          isRead: true, 
          readAt: new Date() 
        }))
      );
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      setError('Failed to mark all notifications as read');
    }
  }, [currentUser]);

  // Archive notification
  const archive = useCallback(async (notificationId: string) => {
    try {
      await archiveNotification(notificationId);
      setNotifications(prev => 
        prev.filter(notification => notification.id !== notificationId)
      );
    } catch (err) {
      console.error('Error archiving notification:', err);
      setError('Failed to archive notification');
    }
  }, []);

  // Delete notification
  const deleteNotification = useCallback(async (notificationId: string) => {
    try {
      await deleteNotification(notificationId);
      setNotifications(prev => 
        prev.filter(notification => notification.id !== notificationId)
      );
    } catch (err) {
      console.error('Error deleting notification:', err);
      setError('Failed to delete notification');
    }
  }, []);

  // Create notification
  const create = useCallback(async (
    type: NotificationType,
    title: string,
    message: string,
    data?: any,
    priority: NotificationPriority = 'medium'
  ) => {
    if (!currentUser) throw new Error('User not authenticated');

    try {
      const notificationId = await createNotification(
        currentUser.uid,
        type,
        title,
        message,
        data,
        priority
      );
      return notificationId;
    } catch (err) {
      console.error('Error creating notification:', err);
      setError('Failed to create notification');
      throw err;
    }
  }, [currentUser]);

  // Auto-notification methods
  const notifyNewSubscriber = useCallback(async (subscriberName: string, publicationName: string) => {
    if (!currentUser) throw new Error('User not authenticated');
    return createAutoNotifications.onNewSubscriber(currentUser.uid, subscriberName, publicationName);
  }, [currentUser]);

  const notifySubscriptionCancelled = useCallback(async (subscriberName: string, publicationName: string) => {
    if (!currentUser) throw new Error('User not authenticated');
    return createAutoNotifications.onSubscriptionCancelled(currentUser.uid, subscriberName, publicationName);
  }, [currentUser]);

  const notifyPaymentReceived = useCallback(async (amount: number, subscriberName: string) => {
    if (!currentUser) throw new Error('User not authenticated');
    return createAutoNotifications.onPaymentReceived(currentUser.uid, amount, subscriberName);
  }, [currentUser]);

  const notifyPaymentFailed = useCallback(async (subscriberName: string, amount: number) => {
    if (!currentUser) throw new Error('User not authenticated');
    return createAutoNotifications.onPaymentFailed(currentUser.uid, subscriberName, amount);
  }, [currentUser]);

  const notifyPostPublished = useCallback(async (postTitle: string, subscriberCount: number) => {
    if (!currentUser) throw new Error('User not authenticated');
    return createAutoNotifications.onPostPublished(currentUser.uid, postTitle, subscriberCount);
  }, [currentUser]);

  const notifyRevenueMilestone = useCallback(async (amount: number, milestone: string) => {
    if (!currentUser) throw new Error('User not authenticated');
    return createAutoNotifications.onRevenueMilestone(currentUser.uid, amount, milestone);
  }, [currentUser]);

  // Add notification (simple toast-style notification)
  const addNotification = useCallback((message: string, type: 'success' | 'error' | 'info' | 'warning') => {
    // For now, just log to console - in a real app, you'd show a toast
    // You could also create a persistent notification
    if (currentUser) {
      const notificationType: NotificationType = type === 'error' ? 'system_update' : 
                                                type === 'warning' ? 'security_alert' : 
                                                type === 'success' ? 'post_published' : 'system_update';
      
      create(notificationType, type.charAt(0).toUpperCase() + type.slice(1), message)
        .catch(err => console.error('Failed to create notification:', err));
    }
  }, [currentUser, create]);

  // Refresh notifications
  const refresh = useCallback(async () => {
    await loadNotifications();
  }, [loadNotifications]);

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    archive,
    delete: deleteNotification,
    create,
    addNotification,
    notifyNewSubscriber,
    notifySubscriptionCancelled,
    notifyPaymentReceived,
    notifyPaymentFailed,
    notifyPostPublished,
    notifyRevenueMilestone,
    refresh,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
